import { useMemo, useRef } from 'react';
import {
	Box,
	Checkbox,
	CircularProgress,
	FilledInput,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';
import { useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { MultiSelectPickerProps } from './types';

/**
 * Generic MultiSelectPicker Component
 * A reusable multi-select dropdown with checkboxes that can be used for any domain.
 *
 * @template TValue - The type of the value (string or number)
 * @template TOption - The type of the option object
 */
export default function MultiSelectPicker<TValue extends string | number, TOption>(
	props: MultiSelectPickerProps<TValue, TOption>
) {
	const {
		value,
		onChange,
		options,
		getOptionValue,
		renderOptionLabel,
		label,
		title,
		hideTitle = false,
		required = false,
		disabled = false,
		loading = false,
		noOptions = false,
		noOptionsText,
		error = false,
		helperText,
		direction = 'rtl',
	} = props;

	const theme = useTheme();
	const t = useAppSelector(selectTranslations);
	const selectRef = useRef<HTMLDivElement | null>(null);

	// Build lookup map for options by value
	const optionMap = useMemo(() => {
		const map: Record<string | number, TOption> = {};
		options.forEach((opt) => {
			const optValue = getOptionValue(opt);
			map[optValue] = opt;
		});
		return map;
	}, [options, getOptionValue]);

	// Render the comma-separated list of selected labels with truncation
	const renderMultiValue = (selected: TValue[]) => {
		if (!selected.length) return '';
		const validItems = selected.filter((val) => optionMap[val]);
		const count = validItems.length;

		// Show count if more than 3 items selected
		if (count > 3) {
			const firstThree = validItems
				.slice(0, 3)
				.map((val) => renderOptionLabel(optionMap[val]))
				.join(', ');
			return `${firstThree} (+${count - 3})`;
		}

		return validItems.map((val) => renderOptionLabel(optionMap[val])).join(', ');
	};

	// Normalize value to array
	const selectValue: TValue[] = useMemo(() => {
		return Array.isArray(value) ? value : value ? [value] : [];
	}, [value]);

	// Handle selection change
	const handleChange = (event: SelectChangeEvent<TValue[]>) => {
		const {
			target: { value: selected },
		} = event;

		// Handle string conversion from select (MUI returns comma-separated string for multiple)
		let nextSelection: TValue[];
		if (typeof selected === 'string') {
			// Determine if values are numbers or strings based on first option
			const firstOptionValue = options.length > 0 ? getOptionValue(options[0]) : '';
			if (typeof firstOptionValue === 'number') {
				nextSelection = selected.split(',').map((s) => Number(s) as TValue);
			} else {
				nextSelection = selected.split(',') as TValue[];
			}
		} else {
			nextSelection = selected as TValue[];
		}

		onChange(nextSelection);
	};

	// Generate unique ID for accessibility
	const labelId = useMemo(() => `multi-select-picker-label-${Math.random().toString(36).substr(2, 9)}`, []);

	return (
		<Stack spacing={1} sx={{ minWidth: 0, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
			{!hideTitle && title && (
				<Typography variant="h6" pb={1}>
					{title}
				</Typography>
			)}
			<Box sx={{ overflow: 'hidden', width: '100%', minWidth: 0, maxWidth: '100%', position: 'relative' }}>
				<FormControl
					fullWidth
					disabled={disabled || loading || noOptions}
					error={error || noOptions}
					required={required}
					sx={{ overflow: 'hidden', minWidth: 0 }}
				>
					<InputLabel
						id={labelId}
						variant="filled"
						sx={{
							color: theme.palette.root.basicDeeoGreen,
							fontFamily: 'RubikRegular',
							'&.Mui-focused': { color: theme.palette.primary.main },
						}}
					>
						{label}
					</InputLabel>
					<Select
						labelId={labelId}
						multiple
						variant="filled"
						value={selectValue}
						onChange={handleChange}
						input={
							<FilledInput
								sx={{
									overflow: 'hidden',
									'& .MuiFilledInput-input': {
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
									},
								}}
							/>
						}
						ref={selectRef}
						renderValue={renderMultiValue}
						sx={{
							borderRadius: '8px',
							border: `1px solid ${theme.palette.root.darkGrayed}`,
							backgroundColor: 'inherit',
							width: '100%',
							minWidth: 0,
							overflow: 'hidden',
							'&:before, &:after': {
								borderBottom: 0,
							},
							'&:hover:not(.Mui-disabled):before': {
								borderBottom: 0,
							},
							'& .MuiSelect-select': {
								overflow: 'hidden !important',
								textOverflow: 'ellipsis !important',
								whiteSpace: 'nowrap !important',
								display: 'block',
								minWidth: 0,
							},
						}}
						MenuProps={{
							PaperProps: {
								style: {
									direction,
									maxHeight: 400,
									width: selectRef.current ? selectRef.current.clientWidth : undefined,
								},
							},
						}}
					>
						{options.map((option) => {
							const optValue = getOptionValue(option);
							return (
								<MenuItem
									key={String(optValue)}
									value={optValue}
									sx={{
										display: 'flex',
										justifyContent: 'flex-start',
										gap: 1,
										typography: 'subtitle1',
										fontSize: theme.typography.subtitle1?.fontSize,
									}}
								>
									<Checkbox checked={selectValue.includes(optValue)} />
									<Box
										sx={{
											flex: 1,
											lineHeight: 1.4,
											whiteSpace: 'normal',
											wordBreak: 'break-word',
											overflowWrap: 'anywhere',
											textAlign: direction === 'ltr' ? 'right' : 'left',
										}}
									>
										{renderOptionLabel(option)}
									</Box>
								</MenuItem>
							);
						})}
					</Select>
					{loading && (
						<FormHelperText sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							<CircularProgress size={14} /> {t('validations.loading.text')}
						</FormHelperText>
					)}
					{!loading && noOptions && <FormHelperText>{noOptionsText}</FormHelperText>}
					{!loading && !noOptions && helperText && <FormHelperText>{helperText}</FormHelperText>}
				</FormControl>
			</Box>
		</Stack>
	);
}
